import type { Point } from '../types';
import { curveFromExtremes } from '../lib/tideCurve.ts';

const BASE = 'https://api.tidesandcurrents.noaa.gov/api/prod/datagetter';

export interface TideEvent {
  t: Date;
  /** Height in feet above MLLW. */
  heightFt: number;
  kind: 'high' | 'low';
}

/**
 * High/low tide predictions. Sailing context for the wind readings — the Bay's
 * current runs hard enough that wind-against-tide is the whole story some days.
 */
export async function tidesToday(stationId: string): Promise<TideEvent[]> {
  const qs = new URLSearchParams({
    product: 'predictions',
    datum: 'MLLW',
    interval: 'hilo',
    date: 'today',
    units: 'english',
    time_zone: 'gmt',
    format: 'json',
    application: 'sfbaywind',
    station: stationId,
  });

  const res = await fetch(`${BASE}?${qs}`);
  if (!res.ok) throw new Error(`Tides HTTP ${res.status}`);

  const body = await res.json();
  if (body.error || !Array.isArray(body.predictions)) return [];

  return body.predictions.map((p: { t: string; v: string; type: string }) => ({
    t: new Date(`${p.t.replace(' ', 'T')}:00Z`),
    heightFt: Number(p.v),
    kind: p.type === 'H' ? ('high' as const) : ('low' as const),
  }));
}

/** The next high or low after `now`, or null once the day's events have passed. */
export function nextTide(events: TideEvent[], now: Date = new Date()): TideEvent | null {
  return events.find((e) => e.t.getTime() > now.getTime()) ?? null;
}

export interface TideDay {
  /** 30-minute predicted heights across the window. */
  curve: Point[];
  /** Highs and lows inside the window, for direct labelling. */
  extremes: TideEvent[];
  /**
   * True when the curve was interpolated from the extremes rather than
   * published. Subordinate stations — two thirds of the network — have no
   * harmonic constituents, so NOAA can give their turns but not their shape.
   */
  derived: boolean;
}

/** Hours of already-past tide kept for context before "now". */
const LOOKBEHIND_H = 3;
/** Total window. Leaves 33 h ahead, so a full day of future is always visible. */
const WINDOW_H = 36;

const dayCache = new Map<string, Promise<TideDay>>();

function windowStart(now: Date): Date {
  const d = new Date(now.getTime() - LOOKBEHIND_H * 3600_000);
  d.setMinutes(0, 0, 0);
  return d;
}

/**
 * Predicted tide for one station: the 30-minute curve plus its highs and lows.
 *
 * The window runs from three hours back to 33 hours ahead rather than over the
 * calendar day — asked at 11pm, "today" would have shown an hour of future and
 * nothing else.
 *
 * `lst_ldt` is the station's own local clock, which is also the display clock,
 * so these parse without an offset. Predictions are fixed, so the result is
 * cached per station and per window start; the hourly key lets a long session
 * roll forward instead of showing an ageing window.
 */
export function tideDay(stationId: string, now: Date = new Date()): Promise<TideDay> {
  const begin = windowStart(now);
  const key = `${stationId}:${begin.toISOString()}`;

  const hit = dayCache.get(key);
  if (hit) return hit;

  const pending = fetchDay(stationId, begin).catch((err) => {
    dayCache.delete(key); // Let a transient failure be retried.
    throw err;
  });
  dayCache.set(key, pending);
  return pending;
}

function stamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

async function fetchDay(stationId: string, begin: Date): Promise<TideDay> {
  const base = {
    product: 'predictions',
    datum: 'MLLW',
    begin_date: stamp(begin),
    range: String(WINDOW_H),
    units: 'english',
    time_zone: 'lst_ldt',
    format: 'json',
    application: 'sfbaywind',
    station: stationId,
  };

  /**
   * CO-OPS answers 403 to a burst — clicking through several markers quickly is
   * enough to trip it. One paced retry clears it; the cache stops it recurring
   * for a station already loaded.
   */
  const get = async (interval: string, attempt = 0): Promise<Array<{ t: string; v: string; type?: string }>> => {
    const res = await fetch(`${BASE}?${new URLSearchParams({ ...base, interval })}`);

    if (!res.ok) {
      if (attempt === 0 && (res.status === 403 || res.status === 429 || res.status >= 500)) {
        await new Promise((r) => setTimeout(r, 900));
        return get(interval, attempt + 1);
      }
      throw new Error(`Tides HTTP ${res.status}`);
    }

    const body = await res.json();
    if (body.error) throw new Error(body.error.message ?? 'No tide predictions');
    return (body.predictions ?? []) as Array<{ t: string; v: string; type?: string }>;
  };

  /*
   * The curve is optional; the extremes are not.
   *
   * A subordinate station answers `interval=hilo` and nothing else — every
   * other interval returns "No Predictions data was found", which is a
   * misleading way of saying the station has no harmonic constituents. Letting
   * that failure reject the pair is what made two thirds of the national tide
   * stations report no data at all, when NOAA had published their highs and
   * lows perfectly well.
   */
  const [curveRows, hiloRows] = await Promise.all([
    get('30').catch(() => [] as Array<{ t: string; v: string; type?: string }>),
    get('hilo'),
  ]);

  const extremes = hiloRows.map((p) => ({
    t: local(p.t),
    heightFt: Number(p.v),
    kind: p.type === 'H' ? ('high' as const) : ('low' as const),
  }));

  const published = curveRows.map((p) => ({ t: local(p.t), v: Number(p.v) }));
  const derived = published.length === 0;

  return {
    curve: derived ? curveFromExtremes(extremes) : published,
    derived,
    extremes,
  };
}

function local(t: string): Date {
  return new Date(`${t.replace(' ', 'T')}:00`);
}
